import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { ShieldAlert, AlertTriangle, CheckCircle2, Ruler, PackageCheck, ExternalLink } from 'lucide-react';

const proibidos = [
  ['Explosivos e muito inflamáveis', 'Gasolina, álcool, solventes, tintas, fogos de artifício, cápsulas de gás, cilindros pressurizados, airbags.'],
  ['Gases sob pressão', 'Gás de cozinha, isqueiros, extintores, panelas de pressão fechadas, bolas cheias.'],
  ['Produtos radioativos', 'Como detectores de fumaça radioativos e materiais nucleares.'],
  ['Perecíveis', 'Carnes, peixes, ovos e alimentos que estragam facilmente.'],
  ['Corrosivos e nocivos', 'Ácidos, soda cáustica, agrotóxicos, baterias automotivas, produtos de limpeza concentrados.'],
  ['Substâncias que cheiram mal ou podem causar dano', 'Materiais em decomposição e objetos cortantes sem proteção.'],
  ['Drogas e medicamentos controlados', 'Entorpecentes, psicotrópicos e remédios controlados sem autorização.'],
  ['Dinheiro', 'Cédulas e moedas em circulação, inclusive estrangeiras. Para valores, os Correios oferecem o Vale Postal.'],
  ['Armas, munições e produtos controlados pelo Exército', 'Só podem ser postados por fabricantes e lojistas autorizados, e não por este aplicativo.'],
  ['Conteúdo ofensivo ou ilegal', 'Mensagens ameaçadoras, ofensivas ou discriminatórias, produtos falsificados, animais silvestres e itens proibidos por autoridades (Anvisa, Ibama etc.).'],
  ['Cigarros e derivados do tabaco', 'Só podem ser enviados entre pessoas físicas, sem finalidade comercial. Não podem ser enviados em vendas.'],
];

const restritos = [
  ['Líquidos não perigosos', 'Cosméticos, produtos de higiene e limpeza, bebidas, suplementos, tintas de impressora. Embalagem que não vaze, conteúdo declarado e a frase "Contém líquido não perigoso" nas três faces da caixa. Bebidas alcoólicas e lubrificantes automotivos só seguem por via terrestre, então o prazo pode ser maior.'],
  ['Cosméticos e medicamentos sem receita', 'Perfumes, cremes, xampus e remédios comuns, com embalagem que proteja de vazamento e o conteúdo bem descrito. Sem acetona, éter, formol nem produtos inflamáveis ou tóxicos.'],
  ['Aerossol não inflamável', 'Só por via terrestre, com embalagem resistente. Aerossol com álcool, solvente ou símbolo de inflamável não é aceito.'],
  ['Celulares, notebooks, tablets e outros eletrônicos com bateria de lítio', 'Somente com a bateria instalada no aparelho. Baterias soltas, danificadas ou retiradas de circulação não podem ser enviadas, e há limite de quantidade por remessa.'],
  ['Sementes e plantas', 'Exigem documentação (como nota fiscal e, em alguns casos, PTV ou Renasem) e a embalagem certa. Plantas vivas só seguem por SEDEX.'],
  ['Cilindros novos e vazios', 'Totalmente vazios, limpos e sem pressão, só por via terrestre.'],
  ['Cinzas de cremação', 'Em recipiente lacrado, dentro de caixa resistente, com o conteúdo declarado.'],
  ['Objetos de valor', 'Joias, metais preciosos e itens de alto valor pedem o serviço de Valor Declarado dos Correios, que este aplicativo ainda não oferece. Nesses casos, poste em uma agência.'],
];

const naoAtendemos = 'Material biológico, medicamentos controlados, animais vivos, ovos embrionados e outros itens que exigem contrato específico ou autorização de órgãos públicos.';

const Bloco = ({ cor, icone: Icone, titulo, subtitulo, itens }) => (
  <section className="bg-white rounded-2xl border border-slate-100 inn-shadow-card p-6 lg:p-8">
    <div className="flex items-start gap-3 mb-5">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${cor}`}><Icone className="w-5 h-5" /></div>
      <div>
        <h2 className="text-xl font-extrabold text-slate-900">{titulo}</h2>
        <p className="text-sm text-slate-600 mt-0.5">{subtitulo}</p>
      </div>
    </div>
    <ul className="grid md:grid-cols-2 gap-x-8 gap-y-4">
      {itens.map(([t, d]) => (
        <li key={t}>
          <p className="font-semibold text-slate-900 text-sm">{t}</p>
          <p className="text-sm text-slate-600 leading-relaxed">{d}</p>
        </li>
      ))}
    </ul>
  </section>
);

const RegrasEnvioPage = () => (
  <div>
    <Navbar />
    <main className="bg-gradient-to-b from-[#EAF1FF]/40 to-white py-12 lg:py-16">
      <div className="max-w-5xl mx-auto px-5 lg:px-8 space-y-6">
        <div className="text-center mb-4">
          <span className="inn-stat-pill">Antes de enviar</span>
          <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900">
            O que <span className="inn-text-gradient">pode</span> e o que <span className="inn-text-gradient">não pode</span> enviar
          </h1>
          <p className="mt-3 text-slate-600 max-w-2xl mx-auto">
            Resumo das regras dos Correios para envios nacionais. Confira o conteúdo do seu pacote antes de emitir a etiqueta.
          </p>
        </div>

        <Bloco cor="bg-red-50 text-red-600" icone={ShieldAlert} titulo="Proibido" subtitulo="Não pode ser enviado em nenhuma hipótese. O pacote pode ser recusado, apreendido ou destruído, sem devolução nem indenização." itens={proibidos} />
        <Bloco cor="bg-amber-50 text-amber-600" icone={AlertTriangle} titulo="Permitido com regras" subtitulo="Pode ser enviado se cumprir as exigências de embalagem, declaração e documentos." itens={restritos} />

        <section className="bg-white rounded-2xl border border-slate-100 inn-shadow-card p-6 lg:p-8">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 bg-slate-100 text-slate-600"><AlertTriangle className="w-5 h-5" /></div>
            <div>
              <h2 className="text-xl font-extrabold text-slate-900">Não atendemos por aqui</h2>
              <p className="text-sm text-slate-600 mt-0.5">{naoAtendemos}</p>
            </div>
          </div>
        </section>

        <section className="bg-white rounded-2xl border border-slate-100 inn-shadow-card p-6 lg:p-8">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 bg-[#EAF1FF] text-[#2A5BC7]"><Ruler className="w-5 h-5" /></div>
            <h2 className="text-xl font-extrabold text-slate-900 pt-2">Limites de peso e medidas</h2>
          </div>
          <ul className="grid sm:grid-cols-2 gap-3 text-sm text-slate-700">
            <li className="bg-slate-50 rounded-xl p-4"><strong className="text-slate-900">Peso:</strong> de 1 g até 30 kg (30.000 g).</li>
            <li className="bg-slate-50 rounded-xl p-4"><strong className="text-slate-900">Comprimento:</strong> de 11 cm até 105 cm.</li>
            <li className="bg-slate-50 rounded-xl p-4"><strong className="text-slate-900">Largura:</strong> de 6 cm até 105 cm.</li>
            <li className="bg-slate-50 rounded-xl p-4"><strong className="text-slate-900">Altura:</strong> de 0,4 cm até 105 cm.</li>
          </ul>
          <p className="text-xs text-slate-500 mt-3">Pacotes grandes e leves podem ser cobrados pelo peso cubado (calculado pelas medidas), se ele for maior que o peso real.</p>
        </section>

        <section className="bg-white rounded-2xl border border-slate-100 inn-shadow-card p-6 lg:p-8">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 bg-emerald-50 text-emerald-600"><PackageCheck className="w-5 h-5" /></div>
            <h2 className="text-xl font-extrabold text-slate-900 pt-2">Dicas para enviar sem problemas</h2>
          </div>
          <ul className="space-y-2 text-sm text-slate-700">
            {[
              'Descreva o conteúdo com clareza no campo "Conteúdo" e informe o valor real dos itens.',
              'Embale para o pacote chegar inteiro, mesmo com mudanças de temperatura, umidade e pressão.',
              'Se houver risco de vazamento, use um recipiente interno lacrado e uma embalagem externa resistente.',
              'Cole a etiqueta em uma face lisa da caixa, sem dobras, e sem cobrir o código de barras.',
              'Em caso de dúvida sobre um item, consulte a página oficial dos Correios antes de emitir.',
            ].map((t) => (
              <li key={t} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" /><span>{t}</span></li>
            ))}
          </ul>
        </section>

        <div className="bg-[#EAF1FF] rounded-2xl p-5 text-sm text-slate-700">
          <p><strong className="text-slate-900">Importante:</strong> este é um resumo informativo baseado nas regras dos Correios, que podem mudar. A lista oficial e atualizada é a que vale, e quem envia é responsável pelo conteúdo do pacote.</p>
          <a href="https://www.correios.com.br/enviar/proibicoes-e-restricoes" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-2 font-semibold text-[#2A5BC7] hover:text-[#F77820]">
            Ver a lista oficial dos Correios <ExternalLink className="w-4 h-4" />
          </a>
        </div>

        <div className="text-center pt-2">
          <Link to="/calcular"><Button className="inn-btn-primary h-12 px-7 rounded-full">Calcular meu frete</Button></Link>
        </div>
      </div>
    </main>
    <Footer />
  </div>
);

export default RegrasEnvioPage;
